import { type FetchInfiniteQueryOptions, type InfiniteData, type QueryClient } from '@tanstack/react-query';

import { type TJsonQuery } from '@/packages/JsonQuery';
import { type IQueryStateAppContext } from '@/packages/query-state/shared/types/appContext';
import { extractUndefined } from '@/packages/utils/extractUndefined';
import { getCancellationTokenFromSignal } from '@/packages/utils/getCancellationTokenFromSignal';

import type {
  TSearchOffersDesktopRequest,
  TSearchOffersDesktopResponse_1,
} from '@/repositories/generated/shared/search-offers/v3/search-offers-desktop';

import { type QueryError, type QueryKey, getQueryKey } from './internal/config';
import { fetchSearchOffersDesktopService } from './internal/fetchService';

export type TStatus = 'added' | 'available' | 'unavailable';

export class FetchSearchOffersDesktopLoadService {
  private readonly queryClient: QueryClient;

  private readonly queryKey: QueryKey;

  public constructor(queryClient: QueryClient, params?: string[]) {
    this.queryClient = queryClient;

    this.queryKey = getQueryKey(params);
  }

  public prefetchQuery<TData = TSearchOffersDesktopResponse_1>({
    applicationContext,
    jsonQuery,
    options,
  }: {
    applicationContext: IQueryStateAppContext;
    jsonQuery: TJsonQuery;
    options?: FetchInfiniteQueryOptions<TSearchOffersDesktopResponse_1, QueryError, TData, QueryKey>;
  }): Promise<void> {
    const parameters: TSearchOffersDesktopRequest = { jsonQuery };

    return this.queryClient.prefetchInfiniteQuery<TSearchOffersDesktopResponse_1, QueryError, TData, QueryKey>({
      queryFn: ({ signal }) =>
        fetchSearchOffersDesktopService({
          applicationContext,
          cancellationToken: getCancellationTokenFromSignal(signal),
          parameters,
        }),
      queryKey: this.queryKey,
      ...options,
    });
  }

  public fetchQuery<TData = TSearchOffersDesktopResponse_1>(
    applicationContext: IQueryStateAppContext,
    jsonQuery: TJsonQuery,
    options?: FetchInfiniteQueryOptions<TSearchOffersDesktopResponse_1, QueryError, TData, QueryKey>,
  ): Promise<InfiniteData<TData>> {
    const parameters: TSearchOffersDesktopRequest = { jsonQuery };

    return this.queryClient.fetchInfiniteQuery<TSearchOffersDesktopResponse_1, QueryError, TData, QueryKey>({
      queryFn: ({ signal }) =>
        fetchSearchOffersDesktopService({
          applicationContext,
          cancellationToken: getCancellationTokenFromSignal(signal),
          parameters,
        }),
      queryKey: this.queryKey,
      ...options,
    });
  }

  public getQueryData(): InfiniteData<TSearchOffersDesktopResponse_1> | null {
    return extractUndefined(this.queryClient.getQueryData<InfiniteData<TSearchOffersDesktopResponse_1>>(this.queryKey));
  }

  public getFlatQueryData(): InfiniteData<TSearchOffersDesktopResponse_1> | null {
    const data = this.getQueryData();

    if (!data) {
      return null;
    }

    return {
      pages: data.pages.reduce<TSearchOffersDesktopResponse_1[]>(
        (acc, page) => [{ ...page, offers: [...(acc[0]?.offers || []), ...page.offers] }],
        [],
      ),
      pageParams: data.pageParams,
    };
  }

  public setQueryData(
    updater: InfiniteData<TSearchOffersDesktopResponse_1>,
  ): InfiniteData<TSearchOffersDesktopResponse_1> | null {
    return extractUndefined(
      this.queryClient.setQueryData<InfiniteData<TSearchOffersDesktopResponse_1>>(this.queryKey, updater),
    );
  }

  public updateFavoriteByOfferId(id: number, newFavoriteValue: boolean): void {
    const prev = this.getQueryData();

    if (prev) {
      const next: InfiniteData<TSearchOffersDesktopResponse_1> = JSON.parse(JSON.stringify(prev));

      this.setQueryData({
        ...next,
        pages: next.pages.map(page => ({
          ...page,
          offers: page.offers.map(offer => {
            if (offer.id === id) {
              offer.data.extendedProperties.isFavorite = newFavoriteValue;
            }

            return offer;
          }),
        })),
      });
    }
  }

  public removeOfferById(id: number): void {
    const prev = this.getQueryData();

    if (prev) {
      const next: InfiniteData<TSearchOffersDesktopResponse_1> = JSON.parse(JSON.stringify(prev));

      this.setQueryData({
        ...next,
        pages: next.pages.map(page => ({ ...page, offers: page.offers.filter(offer => offer.id !== id) })),
      });
    }
  }

  public updateCompareByOfferId(id: number, newCompareValue: { status: TStatus; description: string }): void {
    const prev = this.getQueryData();

    if (prev) {
      const next: InfiniteData<TSearchOffersDesktopResponse_1> = JSON.parse(JSON.stringify(prev));

      this.setQueryData({
        ...next,
        pages: next.pages.map(page => ({
          ...page,
          offers: page.offers.map(offer => {
            if (offer.id === id) {
              offer.data.comparisonStatus = { ...newCompareValue };
            }

            return offer;
          }),
        })),
      });
    }
  }
}
